import React, { useContext, useState } from 'react';
import { Control, Controller, UseFormRegister } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Button, FormControl, SvgIcon, TextField } from '@material-ui/core';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { ConditioningType, MaterialFormInterface } from 'types';
import { colors, fonts } from 'theme';
import { AppContext } from 'context';
import { useMaterialStandards } from 'hooks';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import { CancelIcon, ValidateIcon } from '../Icons';

const useStyles = makeStyles({
  selectInTd: {
    width: '100%',
  },
  rowStyle: {
    cursor: 'pointer',
    backgroundColor: colors.white,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: '10px',
    borderLeft: `10px solid ${colors.primary}`,
    height: '50px',
    '> div': {
      padding: '0 15px',
      borderBottom: 'none',
      fontFamily: fonts.secondary,
      '&:first-child': {
        paddingLeft: '34px',
      },
    },
  },
  muiTextFieldRoot: {
    width: '98%',
    '& input': {
      textAlign: 'right',
      fontFamily: fonts.secondary,
    },
  },
  cellStyle20: {
    width: '20%',
    padding: '0 15px',
  },
  cellStyle10: {
    width: '10%',
    padding: '0 15px',
    textTransform: 'capitalize',
  },
  cellStyle15: {
    width: '15%',
    padding: '0 15px',
    textTransform: 'capitalize',
  },
  cellStyle5: {
    width: '5%',
    padding: '1px',
  },
  cellActions: {
    borderLeft: `1px solid ${colors.greyLighten20}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '45px',
    width: '15%',
  },
  averageWeightColumn: {
    width: '10%',
  },
  actionButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
});

export interface MaterialFormProps {
  onCancel?: () => void | undefined;
  register: UseFormRegister<MaterialFormInterface>;
  control: Control<MaterialFormInterface, object>;
  isSubmitting?: Boolean;
}

const MaterialForm: React.FC<MaterialFormProps> = ({ onCancel, control, register, isSubmitting }) => {
  const context = useContext(AppContext);
  const classes = useStyles();
  const { t } = useTranslation();

  const materialStandardList = useMaterialStandards();
  const materialStandardChoices =
    materialStandardList?.value?.filter(function (materialStandard) {
      return 'Refus_CS' !== materialStandard.code;
    }) ?? [];

  const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;
  const [averageWeightDisabled, setAverageWeightDisabled] = useState<boolean>(true);

  return (
    <div className={classes.rowStyle}>
      <div className={classes.cellStyle20}>
        <input
          hidden
          {...register(`sortingCenter`, {
            value: sortingCenterId,
          })}
        />
        <FormControl>
          <Controller
            name="shortName"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <TextField
                id="shortName"
                inputRef={ref}
                {...fieldProps}
                helperText={fieldState.error?.message}
                error={fieldState.invalid}
                inputProps={{ maxLength: 100 }}
              />
            )}
          />
        </FormControl>
      </div>
      <div className={classes.cellStyle15}>
        <FormControl>
          <Controller
            name="fullName"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <TextField
                id="fullName"
                inputRef={ref}
                {...fieldProps}
                helperText={fieldState.error?.message}
                error={fieldState.invalid}
                inputProps={{ maxLength: 100 }}
              />
            )}
          />
        </FormControl>
      </div>
      <div className={classes.cellStyle15}>
        <Controller
          name="standard"
          control={control}
          render={({ field: { ref, ...inputProps }, fieldState }) => (
            <SelectWithErrorField
              className={classes.selectInTd}
              id="standard"
              choices={materialStandardChoices}
              choiceDisplayKey="fullName"
              choiceValueKey="@id"
              error={fieldState.invalid}
              errorMessage={fieldState.error?.message}
              inputRef={ref}
              InputLabelProps={{ required: true }}
              variant="standard"
              {...inputProps}
            />
          )}
        />
      </div>

      <div className={classes.cellStyle15}>
        <Controller
          name="conditioningType"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <SelectWithErrorField
              className={classes.selectInTd}
              label={t('materials.form.conditioningType')}
              choices={[
                { id: ConditioningType.Ball, name: t(`conditioningType.${ConditioningType.Ball}`) },
                { id: ConditioningType.Bulk, name: t(`conditioningType.${ConditioningType.Bulk}`) },
                { id: ConditioningType.Package, name: t(`conditioningType.${ConditioningType.Package}`) },
              ]}
              choiceDisplayKey="name"
              choiceValueKey="id"
              error={fieldState.invalid}
              errorMessage={fieldState.error?.message}
              inputRef={ref}
              InputLabelProps={{ required: true }}
              variant="standard"
              {...inputProps}
              onChange={e => {
                onChange(e);
                setAverageWeightDisabled(ConditioningType.Bulk.toString() === e?.target?.value);
              }}
            />
          )}
        />
      </div>
      <div className={classes.cellStyle5} />

      <div className={classes.averageWeightColumn}>
        {!averageWeightDisabled && (
          <Controller
            name={`averageWeight`}
            control={control}
            shouldUnregister
            render={({ field: { ref, ...inputProps }, fieldState }) => (
              <WeightInputField
                className={classes.muiTextFieldRoot}
                inputRef={ref}
                {...inputProps}
                helperText={fieldState.error?.message}
                error={fieldState.invalid}
              />
            )}
          />
        )}
      </div>
      <div className={classes.cellStyle5} />
      <div className={classes.cellActions}>
        <div className={classes.actionButtonContainer}>
          <Button color="primary" onClick={onCancel}>
            <SvgIcon component={CancelIcon} fontSize="small" />
          </Button>
          <Button color="primary" onClick={(e: React.MouseEvent) => e.stopPropagation()} type="submit">
            <SvgIcon component={ValidateIcon} fontSize="small" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MaterialForm;
